/**
 * Phone Shifts tab (docs/mobile-design.md §4.3-4.5), shared by mobile web and React Native: the agenda grouped by day,
 * what a shift row and the shift detail offer, the swipe-to-confirm rule, and the time off sheet's balance and
 * "who else is off". Pure TS.
 */
import { DateTime } from 'luxon';
import type { DropPolicy, LeaveBalance, Shift, SwapRequest, TeamSchedule } from '../workforce/types';
import { breakText, breaksForDisplay } from '../workforce/breaks';

export interface AgendaDay<S extends Pick<Shift, 'local_date' | 'starts_at'>> { date: string; label: string; today: boolean; shifts: S[] }
/** My shifts as an agenda: days that have shifts, in order, labelled Today / Tomorrow / "Wed 21 Oct" (one line). */
export function shiftAgenda<S extends Pick<Shift, 'local_date' | 'starts_at' | 'status'>>(shifts: S[], today: string): AgendaDay<S>[] {
  const by = new Map<string, S[]>();
  for (const s of shifts) if (s.status !== 'cancelled') by.set(s.local_date, [...(by.get(s.local_date) ?? []), s]);
  const tomorrow = DateTime.fromISO(today).plus({ days: 1 }).toISODate();
  return [...by.entries()].sort(([a], [b]) => a.localeCompare(b)).map(([date, list]) => ({
    date, today: date === today, shifts: list.sort((a, b) => a.starts_at.localeCompare(b.starts_at)),
    label: date === today ? 'Today' : date === tomorrow ? 'Tomorrow' : DateTime.fromISO(date).setLocale('en-GB').toFormat('ccc d LLL'),
  }));
}

export type ShiftPhase = 'past' | 'now' | 'upcoming';
export function shiftPhase(s: Pick<Shift, 'starts_at' | 'ends_at'>, now = Date.now()): ShiftPhase {
  return Date.parse(s.ends_at) <= now ? 'past' : Date.parse(s.starts_at) <= now ? 'now' : 'upcoming';
}
/** Confirm shows on upcoming, published, unconfirmed shifts. */
export const canConfirmShift = (s: Pick<Shift, 'starts_at' | 'ends_at' | 'status' | 'confirmed_at'>, now = Date.now()) =>
  s.status === 'published' && !s.confirmed_at && shiftPhase(s, now) !== 'past';
/** "Confirm all" appears when two or more shifts in view still need confirming. */
export function confirmAllIds(shifts: Pick<Shift, 'id' | 'starts_at' | 'ends_at' | 'status' | 'confirmed_at'>[], now = Date.now()): string[] {
  const ids = shifts.filter(s => canConfirmShift(s, now)).map(s => s.id);
  return ids.length >= 2 ? ids : [];
}

export interface ShiftDetailActions { confirm: boolean; swap: boolean; offer: boolean; drop: Exclude<DropPolicy, 'off'> | null }
/** The bottom bar of the shift detail: Swap and Offer when swaps are allowed, Drop per the position's policy. Never on past shifts. */
export function shiftActions(s: Pick<Shift, 'starts_at' | 'ends_at' | 'status' | 'confirmed_at'>, o: { allowSwaps: boolean; dropPolicy?: DropPolicy | null; now?: number }): ShiftDetailActions {
  const now = o.now ?? Date.now();
  if (shiftPhase(s, now) !== 'upcoming' || s.status !== 'published') return { confirm: canConfirmShift(s, now), swap: false, offer: false, drop: null };
  const drop = o.dropPolicy ?? 'approval';
  return { confirm: canConfirmShift(s, now), swap: o.allowSwaps, offer: o.allowSwaps, drop: drop === 'off' ? null : drop };
}

/**
 * Swipe right on a row = Confirm (§6 gestures). A mostly-horizontal drag past 35% of the row (at least 80px) commits;
 * anything else snaps back. The row's Confirm button does the same for people who don't swipe.
 */
export function swipeCommits(dx: number, dy: number, width: number): boolean {
  return dx > 0 && Math.abs(dx) > Math.abs(dy) * 1.5 && dx >= Math.max(80, width * 0.35);
}

/** "14.5 days left" (or hours when there's no day length), from the leave balance. */
export function leaveLeftLabel(b: Pick<LeaveBalance, 'remaining_hours' | 'hours_per_day' | 'method'> | null | undefined): string | null {
  if (!b || b.method === 'rolled_up') return null;
  const r = Math.round(b.remaining_hours * 10) / 10;
  if (b.hours_per_day && b.hours_per_day > 0) {
    const d = Math.round((b.remaining_hours / b.hours_per_day) * 2) / 2;
    return `${d} day${d === 1 ? '' : 's'} left`;
  }
  return `${r}h left`;
}

/** Colleagues with approved time off overlapping [from, to] (from the read-only Team view), excluding me. */
export function whoElseOff(team: Pick<TeamSchedule, 'leave' | 'people'> | null | undefined, from: string, to: string, me?: string | null): { user_id: string; display_name: string; start_date: string; end_date: string }[] {
  if (!team || !from) return [];
  const end = to || from;
  const names = new Map(team.people.map(p => [p.user_id, p.display_name]));
  const seen = new Set<string>();
  return team.leave.filter(l => l.user_id !== me && l.start_date <= end && l.end_date >= from)
    .filter(l => (seen.has(l.user_id) ? false : (seen.add(l.user_id), true)))
    .map(l => ({ user_id: l.user_id, display_name: names.get(l.user_id) ?? 'Someone', start_date: l.start_date, end_date: l.end_date }))
    .sort((a, b) => a.start_date.localeCompare(b.start_date) || a.display_name.localeCompare(b.display_name));
}

// ------------------------------------------------------------------------------------------------ t230u: My shifts agenda
/** The Rota tab's switch: Rota (the building's day) or My shifts (my own agenda). Remembered on the device. */
export type RotaTabSeg = 'rota' | 'mine';
export const ROTA_SEG_KEY = 'collab.m.rota.seg';
/** People who can't manage the rota only have My shifts; everyone else gets what they used last (Rota at first). */
export const rotaTabSeg = (canManage: boolean, saved: string | null | undefined, onRota = true): RotaTabSeg =>
  !canManage ? 'mine' : saved === 'mine' && onRota ? 'mine' : 'rota';

/** Infinite scroll in 28-day pages: forward from today, or (Past) backwards from yesterday. */
export const AGENDA_PAGE_DAYS = 28;
export const AGENDA_MAX_PAGES = 13;
export function agendaWindow(today: string, page: number, past: boolean): { from: string; to: string } {
  const t = DateTime.fromISO(today);
  if (!past) { const from = t.plus({ days: page * AGENDA_PAGE_DAYS }); return { from: from.toISODate()!, to: from.plus({ days: AGENDA_PAGE_DAYS - 1 }).toISODate()! }; }
  const to = t.minus({ days: 1 + page * AGENDA_PAGE_DAYS });
  return { from: to.minus({ days: AGENDA_PAGE_DAYS - 1 }).toISODate()!, to: to.toISODate()! };
}

/** My requests still waiting on someone, by shift. */
export type PendingShiftRequest = Pick<SwapRequest, 'id' | 'kind' | 'status' | 'shift_id' | 'requester_id'>;
export function pendingByShift<R extends PendingShiftRequest>(requests: readonly R[], me: string): Map<string, R> {
  const m = new Map<string, R>();
  for (const r of requests) if (r.requester_id === me && (r.status === 'pending_peer' || r.status === 'pending_manager')) m.set(r.shift_id, r);
  return m;
}

export type AgendaChipTone = 'now' | 'todo' | 'ok' | 'pending' | 'muted';
export interface AgendaChip { id: string; label: string; tone: AgendaChipTone }
const REQ_LABEL: Record<PendingShiftRequest['kind'], string> = { drop: 'Drop requested', swap: 'Swap requested', cover: 'Offered' };
/** Status chips on an agenda row, most important first: Now, a waiting request, then To confirm / Confirmed. */
export function agendaChips(s: Pick<Shift, 'starts_at' | 'ends_at' | 'status' | 'confirmed_at' | 'needs_confirmation'>, pending?: Pick<PendingShiftRequest, 'kind'> | null, now = Date.now()): AgendaChip[] {
  const out: AgendaChip[] = [];
  const phase = shiftPhase(s, now);
  if (s.status === 'cancelled') return [{ id: 'cancelled', label: 'Cancelled', tone: 'muted' }];
  if (phase === 'now') out.push({ id: 'now', label: 'Now', tone: 'now' });
  if (pending) out.push({ id: 'request', label: REQ_LABEL[pending.kind], tone: 'pending' });
  if (phase !== 'past') {
    if (s.confirmed_at) out.push({ id: 'confirmed', label: 'Confirmed', tone: 'ok' });
    else if (s.needs_confirmation) out.push({ id: 'confirm', label: 'To confirm', tone: 'todo' });
  }
  return out;
}

export type AgendaAction = 'confirm' | 'swap' | 'offer' | 'drop' | 'cancel_request';
/** What a row's ⋯ offers: Cancel request while one is waiting (nothing else), else the shift's own actions. */
export function agendaActions(a: ShiftDetailActions, pending?: PendingShiftRequest | null): AgendaAction[] {
  if (pending) return ['cancel_request'];
  return [a.confirm && 'confirm', a.swap && 'swap', a.offer && 'offer', a.drop && 'drop'].filter(Boolean) as AgendaAction[];
}

/** "Break 30m" / "Breaks 12:30 30m, 15:00 15m paid" from the shift's planned breaks (local wall clock). */
export function shiftBreaksLabel(s: Pick<Shift, 'starts_at' | 'break_minutes'> & { breaks?: { offset_minutes?: number | null; duration_minutes: number; paid?: boolean }[] | null }, tz: string): string | null {
  if (s.breaks?.length) {
    const at = (off: number) => DateTime.fromISO(s.starts_at, { zone: tz }).plus({ minutes: off }).toFormat('HH:mm');
    // t525u: "Any time" breaks (offset null) read "30 min break, any time" and come after the timed ones.
    return breaksForDisplay(s.breaks).map(b => breakText(b, at)).join(', ');
  }
  return s.break_minutes > 0 ? `${s.break_minutes}m break` : null;
}
